/** Pure derivations for the portal billing surface.
 *
 * The billing fields ride `PortalSummary` once the backend ships its
 * billing block; until then they're absent from the payload. Every
 * derivation here treats "absent" as "billing not in play" so the
 * portal renders exactly as before against an older backend: no
 * Billing tab, no banner, exports unlocked.
 */

export type PortalState = 'onboarding' | 'processing' | 'awaiting_payment' | 'paid_complete';

export interface BillingFields {
  portal_state?: PortalState | null;
  agree_payment_url?: string | null;
  exports_unlocked?: boolean | null;
}

export interface BillingUi {
  /** Render the Billing tab at all. True once billing is in play:
   *  the engagement reached awaiting_payment / paid_complete, or a
   *  payment link exists. */
  showBillingTab: boolean;
  /** Red-dot on the Billing tab + the action-required banner. */
  awaitingPayment: boolean;
  /** The "Paid - thank you" state of the Billing tab. */
  paid: boolean;
  /** Disable Export PDF / CSV with the paywall tooltip. Absent field
   *  (older backend) means unlocked - only an explicit false locks. */
  exportsLocked: boolean;
}

export function billingUi(summary: BillingFields | null | undefined): BillingUi {
  const state = summary?.portal_state ?? null;
  const awaitingPayment = state === 'awaiting_payment';
  const paid = state === 'paid_complete';
  // A locked export is billing in play by definition - without the tab
  // the customer would face disabled downloads with no payment route.
  const exportsLocked = summary?.exports_unlocked === false;
  return {
    showBillingTab: awaitingPayment || paid || exportsLocked || Boolean(summary?.agree_payment_url),
    awaitingPayment,
    paid,
    exportsLocked,
  };
}

export const EXPORTS_LOCKED_TOOLTIP = 'Available after your invoice is paid.';

export const AWAITING_PAYMENT_BANNER =
  'Action required: an invoice is awaiting payment. Exports unlock once it is paid.';
